import React, { useEffect, useState } from "react";
import {
  BiDotsHorizontal,
  BiMinus,
  BiPlay,
  BiSolidMicrophone,
  BiSolidSend
} from "react-icons/bi";

import { IAiAppearance } from "../types";
import BiLouiseSmallGray from "./icons/BiLouiseSmallGray";
import { widgetUtils } from "./utils";
import WidgetText from "./WidgetText";

const TextWidget = ({
  messages,
  data: appearance
}: {
  messages: {
    userType: "bot" | "user";
    data: { type: string; value: string };
  }[];
  data: IAiAppearance;
}) => {
  const [avatarSrc, setAvatarSrc] = useState("");

  const renderMessage = (
    userType: string,
    message: { data: { type: string; value: string } },
    appearance: IAiAppearance
  ) => {
    if (message.data?.type === "audio") {
      if (userType === "bot" && appearance.voiceResponse && appearance.theme)
        return (
          <div
            className={`tw-shadow-md tw-max-w-full tw-min-w-40 tw-pl-3 tw-pr-5 tw-py-3 ${widgetUtils.themeBorderColor(
              appearance.theme
            )} ${widgetUtils.bubbleBorderRadius(
              userType
            )} justify-start items-start gap-3 inline-flex`}
            style={{
              backgroundColor: userType === "bot" ? "#4b5563" : appearance.color
            }}
          >
            <div className="tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
              <div
                className="tw-w-8 tw-h-8 tw-text-white tw-rounded-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-shrink-0"
                style={{
                  backgroundColor:
                    userType === "bot"
                      ? "#374151"
                      : widgetUtils.bgColorMapper[appearance?.color] ??
                        appearance?.color,
                  filter: userType === "bot" ? "brightness(0.9)" : "",
                  color: userType === "bot" ? "black" : "white"
                }}
              >
                <BiPlay
                  className="pl-[2px] mt-[2px]"
                  color="#ffffff"
                  size={25}
                />
              </div>

              <div className="Frame tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-inline-flex">
                <svg
                  style={{ width: "72px", height: "32px", fill: "white" }}
                  data-name="Layer 1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 50 38.05"
                >
                  <title>Audio Wave</title>
                  <path
                    className="Line_1"
                    data-name="Line 1"
                    d="M0.91,15L0.78,15A1,1,0,0,0,0,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H0.91Z"
                  />
                  <path
                    className="Line_2"
                    data-name="Line 2"
                    d="M6.91,9L6.78,9A1,1,0,0,0,6,10V28a1,1,0,1,0,2,0s0,0,0,0V10A1,1,0,0,0,7,9H6.91Z"
                  />
                  <path
                    className="Line_3"
                    data-name="Line 3"
                    d="M12.91,0L12.78,0A1,1,0,0,0,12,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H12.91Z"
                  />
                  <path
                    className="Line_4"
                    data-name="Line 4"
                    d="M18.91,10l-0.12,0A1,1,0,0,0,18,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H18.91Z"
                  />
                  <path
                    className="Line_5"
                    data-name="Line 5"
                    d="M24.91,15l-0.12,0A1,1,0,0,0,24,16v6a1,1,0,0,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H24.91Z"
                  />
                  <path
                    className="Line_6"
                    data-name="Line 6"
                    d="M30.91,10l-0.12,0A1,1,0,0,0,30,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H30.91Z"
                  />
                  <path
                    className="Line_7"
                    data-name="Line 7"
                    d="M36.91,0L36.78,0A1,1,0,0,0,36,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H36.91Z"
                  />
                  <path
                    className="Line_8"
                    data-name="Line 8"
                    d="M42.91,9L42.78,9A1,1,0,0,0,42,10V28a1,1,0,1,0,2,0s0,0,0,0V10a1,1,0,0,0-1-1H42.91Z"
                  />
                  <path
                    className="Line_9"
                    data-name="Line 9"
                    d="M48.91,15l-0.12,0A1,1,0,0,0,48,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H48.91Z"
                  />
                </svg>
              </div>
            </div>
          </div>
        );
      else return null;
    } else
      return (
        <WidgetText
          data={message.data?.value}
          variant={userType as any}
          theme={appearance.theme}
          color={
            widgetUtils.bgColorMapper[appearance?.color] ?? appearance?.color
          }
        />
      );
  };

  useEffect(() => {
    if (typeof appearance.logo === "object") {
      const reader = new FileReader();
      reader.readAsDataURL(appearance.logo);
      reader.onload = () => {
        setAvatarSrc(reader.result as string);
      };
      reader.onerror = (error) => console.log(error);
    } else {
      setAvatarSrc(appearance.logo);
    }
  }, [appearance.logo]);

  const getName = () => {
    if (appearance?.name?.type == "auto") {
      return "Louise";
    } else if (appearance?.name?.type == "other") {
      return appearance?.name?.value;
    } else {
      return "Louise";
    }
  };

  const themeBg =
    appearance.theme === "light"
      ? "tw-bg-gray-50 tw-text-gray-700"
      : "tw-bg-gray-800 tw-text-gray-100";

  const themeBorderColor =
    appearance.theme === "light" ? "tw-border-gray-200" : "tw-border-gray-700";

  return (
    <div
      className={`tw-w-full tw-h-[640px] tw-max-w-[400px] tw-relative tw-flex tw-flex-col ${themeBg} tw-rounded-2xl tw-shadow-xl `}
      id="widget"
    >
      {/* Header */}
      <div
        className={`tw-h-16 tw-rounded-tl-2xl tw-rounded-tr-2xl tw-p-3 tw-shadow-md  ${
          appearance.theme == "light" ? "tw-bg-white" : "tw-bg-gray-700"
        }  tw-justify-between tw-items-center tw-inline-flex`}
      >
        <div className="tw-justify-start tw-items-center tw-gap-3 tw-flex">
          {/* logo */}
          <img
            src={
              avatarSrc == ""
                ? "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                : avatarSrc
            }
            alt={appearance?.name?.value}
            width={40}
            height={40}
            className="tw-min-w-[40px] tw-min-h-[40px] tw-max-w-[40px] tw-max-h-[40px] tw-bg-gradient-to-b  tw-rounded-[99px] tw-object-cover"
          />
          {/* name */}
          <div className="tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7 tw-max-w-[150px] tw-truncate">
            {getName()}
          </div>
        </div>

        {/* actions */}
        <div className="tw-justify-end tw-items-center tw-gap-3 tw-flex">
          <button
            type="button"
            disabled
            className={`tw-p-2 tw-bg-transparent tw-border-none tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex ${
              appearance.theme === "light"
                ? "tw-text-gray-800"
                : "tw-text-gray-50"
            } 
            `}
          >
            <BiDotsHorizontal />
          </button>

          <button
            type="button"
            disabled
            className={`tw-p-2 tw-bg-transparent tw-border-none tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex ${
              appearance.theme === "light"
                ? "tw-text-gray-800"
                : "tw-text-gray-50"
            } 
            `}
          >
            <BiMinus />
          </button>
        </div>
      </div>

      {/* content */}
      <div
        className={`tw-w-full tw-h-full tw-overflow-auto tw-mb-auto tw-p-4 tw-flex tw-flex-col tw-gap-3 ${themeBorderColor} tw-border tw-border-b-0 tw-border-solid tw-relative [&>div:first-child]:tw-mt-auto`}
        id="messages-area"
      >
        {messages.map((message, index) => (
          <React.Fragment key={index}>
            {message.userType == "user" && (
              <div className="tw-user-message tw-max-w-[90%] tw-ml-auto">
                {renderMessage("user", message, appearance)}
              </div>
            )}
            {message.userType == "bot" && (
              <div className="tw-bot-message tw-max-w-[90%] tw-mr-auto">
                {renderMessage("bot", message, appearance)}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Footer */}
      <div
        className={`tw-rounded-bl-2xl tw-rounded-br-2xl  tw-border tw-border-solid tw-border-t-0 ${themeBorderColor} tw-px-4 tw-py-2`}
      >
        <div className="tw-w-full">
          <div className="tw-w-full tw-h-12 tw-bg-transparent tw-justify-between tw-items-center tw-inline-flex tw-gap-2">
            <React.Fragment>
              <div
                className="tw-border tw-border-solid tw-rounded-2xl tw-shadow-sm tw-px-3 tw-py-1 tw-mx-4 tw-w-full"
                style={{
                  borderColor:
                    widgetUtils.bgColorMapper[appearance?.color ?? "#6B7280"] ??
                    appearance?.color,
                  backgroundColor: widgetUtils.themeBackgroundMessage(
                    appearance.theme
                  )
                }}
              >
                <div className="tw-w-full">
                  <div className="tw-relative tw-w-full tw- tw-bg-transparent tw-justify-between tw-items-center tw-inline-flex tw-gap-2">
                    <input
                      readOnly
                      disabled
                      id="TextInput"
                      className="tw-text-gray-800 tw-text-input tw-py-3 tw-px-2 tw-w-full tw-h-full tw-placeholder:text-gray-400 tw-bg-transparent tw-text-[16px] tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-border-0 tw-focus:outline-none"
                      placeholder="Enter your message..."
                      style={{ height: "44px" }}
                    ></input>
                    <div className="tw-contents">
                      <BiSolidSend className="tw-text-black" />
                      {appearance?.isVoiceActive && (
                        <button
                          type="button"
                          disabled
                          style={{
                            backgroundColor:
                              widgetUtils.bgColorMapper[appearance?.color] ??
                              appearance?.color
                          }}
                          className={`tw-rounded-full tw-text-white tw-border-none tw-transition-all tw-p-2 tw-justify-start tw-items-start tw-gap-2.5 tw-flex`}
                        >
                          <BiSolidMicrophone size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          </div>
        </div>

        {appearance.poweredBy && (
          <div
            // className={`tw-h-5 tw-px-2 tw-py-0.5 ${
            //   hasBackground
            //     ? "tw-bg-black/20 tw-rounded-[99px] tw-backdrop-blur-md tw-text-white"
            //     : "tw-text-[#9CA3AF]"
            // } tw-items-center tw-gap-0.5 tw-flex tw-justify-center tw-text-center`}

            className={`tw-h-5 tw-px-2 tw-py-3 tw-my-3 tw-text-[#9CA3AF]
          tw-items-center tw-gap-0.5 tw-flex tw-justify-center tw-text-center`}
          >
            <div className="tw-text-[10px] tw-font-normal tw-font-['Lexend'] tw-leading-[15px]">
              Powered by
            </div>

            {appearance.customPoweredBy ? (
              <React.Fragment>
                {appearance.customPoweredBy?.companyLogo &&
                  (appearance.customPoweredBy?.companyUrl?.startsWith(
                    "https://"
                  ) ? (
                    <a href={`${appearance.customPoweredBy?.companyUrl}`}>
                      <img
                        src={appearance.customPoweredBy?.companyLogo}
                        width={14}
                        height={14}
                        alt={appearance.customPoweredBy?.companyName}
                        className="tw-object-contain"
                      />
                    </a>
                  ) : (
                    <img
                      src={appearance.customPoweredBy?.companyLogo}
                      width={14}
                      height={14}
                      alt={appearance.customPoweredBy?.companyName}
                      className="tw-object-contain"
                    />
                  ))}
                {appearance.customPoweredBy?.companyName &&
                  (appearance.customPoweredBy?.companyUrl?.startsWith(
                    "https://"
                  ) ? (
                    <a href={`${appearance.customPoweredBy?.companyUrl}`}>
                      <span className="tw-font-bold tw-text-[10px] tw-font-['Lexend']">
                        {appearance.customPoweredBy?.companyName}
                      </span>
                    </a>
                  ) : (
                    <span className="tw-font-bold tw-text-[10px] tw-font-['Lexend']">
                      {appearance.customPoweredBy?.companyName}
                    </span>
                  ))}
              </React.Fragment>
            ) : appearance.poweredBy ? (
              <a
                href="https://louisebot.com"
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-center tw-flex tw-items-center tw-justify-center tw-gap-1"
              >
                <div className="tw-justify-start tw-items-center tw-gap-0.5 tw-flex">
                  <BiLouiseSmallGray
                    fill={"#9CA3AF"}
                    // fill={hasBackground ? "#ffffff" : "#9CA3AF"}
                  />
                </div>
                <div className="tw-font-bold tw-text-[10px] tw-font-['Lexend']">
                  LouiseBot
                </div>
              </a>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextWidget;
